import type { QueueRealtimeEvent } from './queue/types';
import { backendClient } from './api/backendClient';

type RetryConfig = {
  initialDelayMs: number;
  maxDelayMs: number;
  multiplier: number;
  jitterMs: number;
};

export type QueueRealtimeSubscriptionOptions = {
  sessionId: string;
  userId: string;
  onEvent: (event: QueueRealtimeEvent) => void;
  onError?: (error: unknown) => void;
  onOpen?: () => void;
  onReconnectScheduled?: (info: { attempt: number; delayMs: number }) => void;
  headers?: Record<string, string>;
  baseUrlOverride?: string;
  retry?: Partial<RetryConfig>;
  fetchImpl?: typeof fetch;
};

export type QueueRealtimeSubscription = {
  close: () => void;
  isConnected: () => boolean;
};

const DEFAULT_RETRY: RetryConfig = {
  initialDelayMs: 1000,
  maxDelayMs: 30_000,
  multiplier: 2,
  jitterMs: 500
};

const resolveBackendUrl = (path: string, overrideBase?: string): string => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = overrideBase ?? backendClient.defaults.baseURL;
  if (!base) {
    return path;
  }

  const trimmedBase = base.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
};

const buildUrl = (sessionId: string, overrideBase?: string): string => {
  const path = `/queue/session/events?sessionId=${encodeURIComponent(sessionId)}`;
  return resolveBackendUrl(path, overrideBase);
};

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

const parseSseFrame = (
  raw: string,
  emit: (event: QueueRealtimeEvent) => void,
  onError?: (error: unknown) => void
) => {
  if (!raw.trim()) {
    return;
  }

  const lines = raw.split('\n');
  const dataLines: string[] = [];

  for (const rawLine of lines) {
    if (!rawLine) continue;
    const line = rawLine.replace(/\r/g, '');
    if (!line || line.startsWith(':')) {
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (!dataLines.length) {
    return;
  }

  const payload = dataLines.join('\n');

  try {
    const parsed = JSON.parse(payload) as QueueRealtimeEvent;
    emit(parsed);
  } catch (error) {
    onError?.(error);
  }
};

export function subscribeToQueueRealtime(
  options: QueueRealtimeSubscriptionOptions
): QueueRealtimeSubscription {
  const {
    sessionId,
    userId,
    onEvent,
    onError,
    onOpen,
    onReconnectScheduled,
    headers,
    baseUrlOverride,
    retry: retryOverrides,
    fetchImpl
  } = options;

  const retry: RetryConfig = {
    ...DEFAULT_RETRY,
    ...retryOverrides
  };

  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;
  let closed = false;
  let connected = false;

  const fetchFn = fetchImpl ?? (typeof fetch !== 'undefined' ? fetch : null);

  if (!fetchFn) {
    throw new Error('fetch is not available in this environment; queue realtime requires the Fetch API.');
  }

  const scheduleReconnect = () => {
    if (closed) {
      return;
    }

    reconnectAttempts += 1;
    const backoff = Math.min(
      retry.initialDelayMs * Math.pow(retry.multiplier, Math.max(0, reconnectAttempts - 1)),
      retry.maxDelayMs
    );
    const jitter = Math.random() * retry.jitterMs;
    const delay = Math.min(retry.maxDelayMs, backoff + jitter);
    onReconnectScheduled?.({ attempt: reconnectAttempts, delayMs: delay });

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, delay);
  };

  const cleanupTimers = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const connect = async () => {
    if (closed) {
      return;
    }

    controller?.abort();
    controller = new AbortController();

    const url = buildUrl(sessionId, baseUrlOverride);

    try {
      const response = await fetchFn(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          'X-User-Id': userId,
          ...(headers ?? {})
        },
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Realtime subscription failed (${response.status})`);
      }

      const stream = response.body;
      if (!stream) {
        throw new Error('Realtime subscription response missing body stream.');
      }

      reconnectAttempts = 0;
      connected = true;
      onOpen?.();

      const reader = stream.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (!closed) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        let delimiterIndex = buffer.indexOf('\n\n');
        while (delimiterIndex !== -1) {
          const rawFrame = buffer.slice(0, delimiterIndex);
          buffer = buffer.slice(delimiterIndex + 2);
          parseSseFrame(rawFrame, onEvent, onError);
          delimiterIndex = buffer.indexOf('\n\n');
        }
      }

      buffer += decoder.decode();
      if (buffer.trim().length) {
        parseSseFrame(buffer, onEvent, onError);
      }
    } catch (error) {
      if (closed || isAbortError(error)) {
        return;
      }
      onError?.(error);
    } finally {
      connected = false;
      controller?.abort();
      controller = null;
    }

    if (!closed) {
      scheduleReconnect();
    }
  };

  void connect();

  return {
    close: () => {
      if (closed) {
        return;
      }
      closed = true;
      cleanupTimers();
      controller?.abort();
      controller = null;
    },
    isConnected: () => connected
  };
}
